import "./scss/description.scss";

import DOMPurify from "dompurify";
import { useEffect, useState } from "react";

import useSidebarContent from "@hooks/useSidebarContent";
import Loader from "@img/Loader.svg";
import { ConditionType } from "@types-runway/condition";

type DescriptionProps = {
  conditionType: ConditionType;
  selectedMedicationName?: string;
};

const disclaimer =
  "This is provided for informational purposes only and does not constitute providing medical advice, nor is the information a substitute for professional medical expertise or treatment.";

const Description = ({ conditionType, selectedMedicationName }: DescriptionProps) => {
  const { getConditionContent, getTreatmentContent } = useSidebarContent();
  const [conditionHtml, setConditionHtml] = useState<string | null>(null);
  const [treatmentHtml, setTreatmentHtml] = useState<string | null>(null);
  const [isTreatmentLoading, setIsTreatmentLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const content = await getConditionContent(conditionType);
      if (content) setConditionHtml(DOMPurify.sanitize(content));
    })();
  }, [conditionType]);

  useEffect(() => {
    let cancelled = false;
    setIsTreatmentLoading(true);

    (async () => {
      const content = await getTreatmentContent(conditionType, selectedMedicationName);
      if (cancelled) return;

      setTreatmentHtml(content ? DOMPurify.sanitize(content) : null);
      setIsTreatmentLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [conditionType, selectedMedicationName]);

  if (!conditionHtml) {
    return (
      <div className="loader">
        <img className="loader-img" src={Loader} alt="Loading" />
      </div>
    );
  }

  return (
    <div className="container">
      <div dangerouslySetInnerHTML={{ __html: conditionHtml }} />

      {isTreatmentLoading && (
        <div className="loader">
          <img className="loader-img" src={Loader} alt="Loading treatment" />
        </div>
      )}

      {!isTreatmentLoading && treatmentHtml && (
        <div dangerouslySetInnerHTML={{ __html: treatmentHtml }} />
      )}

      <p className="disclaimer">
        <em>{disclaimer}</em>
      </p>
    </div>
  );
};

export default Description;
